import React from 'react';
import { DateTime } from 'luxon';
import { Box, SelectChangeEvent, SxProps, Theme, Typography } from '@mui/material';
import EditVisitorDetails from './EditVisitorDetails';
import EditHostDetails from "./EditHostDetails";
import Notes, { INote } from '../../Lists/Notes/Notes';
import { appContext } from '../../../AppContext';
import { IbssComponent } from '../../Core/BaseComponent/IbssComponent';
import { ISelectOption } from '../../Inputs/SelectList/IbssInputDropDown';
import { IUser } from '../../Inputs/UserPicker/UserPicker';
import { VisitorType } from './DataModels';
import { PagedResponse } from '../../../Providers.Api/Models';

export class Visitor extends IbssComponent<IProps, IState>
{
    private visitorDetailsRef = React.createRef<EditVisitorDetails>();
    private hostDetailsRef = React.createRef<EditHostDetails>();
    private get labels() { return appContext().labels; }
    private fieldHelper: VisitorFieldHelper;
    private get showVisitorDetails() { return (this.fieldHelper.has(VisitorField.Name) || this.fieldHelper.has(VisitorField.Company) || this.fieldHelper.has(VisitorField.Email) || this.fieldHelper.has(VisitorField.Pacs)); }
    private get showNotes() { return this.fieldHelper.has(VisitorField.Notes); }
    private get showHostDetails() { return (this.fieldHelper.has(VisitorField.Host) || this.fieldHelper.has(VisitorField.ArrivalLocation) || this.fieldHelper.has(VisitorField.Times) || this.fieldHelper.has(VisitorField.VisitorType)); }
    private pageCacheKey = 'b7f98f87-7cd2-4fd9-8324-4dda177c0537';

    constructor(props: IProps)
    {
        super(props);
        this.fieldHelper = new VisitorFieldHelper(props.fields);
        this.state = {
            visitorTypes: this.props.visitorTypes ?? [],
            areVisitorDetailsValid: !this.showVisitorDetails,
            areHostDetailsValid: !this.showHostDetails,
        };
    }

    public resetValidation(): void
    {
        this.setState({
            areVisitorDetailsValid: !this.showVisitorDetails,
            areHostDetailsValid: !this.showHostDetails,
        });
        this.visitorDetailsRef.current?.resetValidation();
        this.hostDetailsRef.current?.resetValidation();
    }

    public async componentDidMount(): Promise<void>
    {
        if (this.fieldHelper.has(VisitorField.VisitorType) && this.props.visitorTypes == null)
        {
            await this.loadVisitorTypes();
        }
    }

    public async componentDidUpdate(prevProps: IProps): Promise<void> 
    {
        if (prevProps.buildingId !== this.props.buildingId)
        {
            appContext().inMemoryCache.clear(i => i.startsWith(this.pageCacheKey));
            await this.loadVisitorTypes();
        }
    }

    private async loadVisitorTypes(): Promise<void>
    {
        try
        {
            this.props.onLoading?.();
            const filterEndpoint = {
                name: 'ibssApiClientV2.v2.byNodeid.visitorTypes.get',
                options: {
                    nodeId: this.props.buildingId,
                    select: VisitorType,
                }
            };

            const visitorTypesData = await appContext().inMemoryCache.lazyGet(
                `${this.pageCacheKey}_${JSON.stringify(filterEndpoint)}`,
                DateTime.now().plus({ hours: 1 }),
                () => appContext().ibssApiClientV2.v2.byNodeid.visitorTypes.get<PagedResponse<VisitorType[]>>(filterEndpoint.options)
            );

            const fetchedVisitorTypes = visitorTypesData?.value?.map(i => ({ value: i.VisitorTypeId, label: i.Name })) ?? [];
            const visitorTypes = [{ value : "None", label: this.labels.HubLabelNone}, ...fetchedVisitorTypes];
            this.setState({ visitorTypes: visitorTypes });
            if (visitorTypes.length == 1)
            {
                this.props.onChange({ visitorTypeId: visitorTypes[0].value });
            }
        }
        finally
        {
            this.props.onLoaded?.();
        }
    }

    private async handleVisitorDetailsValidate(areVisitorDetailsValid: boolean): Promise<void>
    {
        await this.setStateAsync({ areVisitorDetailsValid: areVisitorDetailsValid });
        this.props.onValidate(areVisitorDetailsValid && this.state.areHostDetailsValid);
    }

    private async handleHostDetailsValidate(areHostDetailsValid: boolean): Promise<void>
    {
        await this.setStateAsync({ areHostDetailsValid: areHostDetailsValid });
        this.props.onValidate(areHostDetailsValid && this.state.areVisitorDetailsValid);
    }

    private handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.props.onChange({ firstName: event.target.value });
    }

    private handleLastNameChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.props.onChange({ lastName: event.target.value });
    }

    private handleCompanyChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.props.onChange({ company: event.target.value });
    }

    private handleEmailChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.props.onChange({ email: event.target.value });
    }

    private handleVisitorTypeChange(event: SelectChangeEvent): void
    {
        this.props.onChange({ visitorTypeId: event.target.value });
    }

    private handlePacsChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.props.onChange({ pacs: event.target.value });
    }

    private handleHostEmailChange(value: string): void
    {
        this.props.onChange({ hostEmail: value });
    }

    private handleHostChange(value: IUser | null): void
    {
        this.props.onChange({ host: value });
    }

    private handleStartDateTimeChange(value: DateTime): void
    {
        this.props.onChange({ visitorStartDate: value });
    }

    private handleEndDateTimeChange(value: DateTime): void
    {
        this.props.onChange({ visitorEndDate: value });
    }

    private handleArrivalLocationChange(event: SelectChangeEvent): void
    {
        this.props.onChange({ arrivalLocationId: event.target.value });
    }

    public render(): JSX.Element
    {
        return <Box
            className="tablePanel"
            sx={this.props.sx}
        >
            {
                this.showVisitorDetails &&
                <EditVisitorDetails
                    ref={this.visitorDetailsRef}
                    fields={this.props.fields}
                    firstName={this.props.firstName}
                    lastName={this.props.lastName}
                    company={this.props.company}
                    email={this.props.email}
                    pacs={this.props.pacs}
                    onValidate={isValid => this.handleVisitorDetailsValidate(isValid)}
                    onFirstNameChange={e => this.handleFirstNameChange(e)}
                    onLastNameChange={e => this.handleLastNameChange(e)}
                    onCompanyChange={e => this.handleCompanyChange(e)}
                    onEmailChange={e => this.handleEmailChange(e)}
                    onPacsChanged={e => this.handlePacsChange(e)}
                    arrivalLocationId={this.props.arrivalLocationId}
                    arrivalLocationOptions={this.props.arrivalLocations}
                    visitorTypeId={this.props.visitorTypeId}
                    visitorTypes={this.state.visitorTypes}
                    onVisitorTypeChange={e => this.handleVisitorTypeChange(e)}
                    onArrivalLocationChange={e => this.handleArrivalLocationChange(e)}
                    isFlex={this.props.isFlex}
                    buildingId={this.props.buildingId}
                    buildingOptions={this.props.buildingOptions}
                    buildingChanged={this.props.buildingChanged}
                    disableFields={this.props.disableFields}
                    emailNotRequired={this.props.emailNotRequired}
                />
            }
            {
                this.showNotes &&
                <>
                    <Box>
                        <h3>{this.labels.funcVisitorNotes_S}</h3>
                        <Notes
                            buildingId={this.props.buildingId}
                            notes={this.props.notes ?? []}
                            onChange={notes => this.props.onChange({ notes: notes })}
                        />
                    </Box>
                    <Typography color="uiText" sx={{ mt: 2 }}>
                        {this.labels.funcVisitorFrontDeskNotes_S}
                    </Typography>
                </>
            }
            {
                (this.showVisitorDetails || this.showNotes) && this.showHostDetails && this.fieldHelper.has(VisitorField.Host) &&
                <hr />
            }
            {
                this.showHostDetails &&
                <EditHostDetails
                    ref={this.hostDetailsRef}
                    fields={this.props.fields}
                    buildingId={this.props.buildingId}
                    hostEmail={this.props.hostEmail}
                    start={this.props.visitorStartDate}
                    end={this.props.visitorEndDate}
                    onValidate={isValid => this.handleHostDetailsValidate(isValid)}
                    onHostEmailChange={e => this.handleHostEmailChange(e)}
                    onHostChange={e => this.handleHostChange(e)}
                    onStartChange={e => this.handleStartDateTimeChange(e)}
                    onEndChange={e => this.handleEndDateTimeChange(e)}
                    disableFields={this.props.disableFields}
                />
            }
        </Box>
    }
}

export interface IProps
{
    fields?: VisitorField;
    sx?: SxProps<Theme>;
    buildingId: number;
    firstName?: string;
    lastName?: string;
    company?: string;
    email?: string;
    isFlex?: boolean;
    visitorTypeId?: string;
    visitorTypes?: ISelectOption<string, string>[];
    pacs?: string;
    notes?: INote[];
    hostEmail?: string;
    arrivalLocationId?: string;
    arrivalLocations?: ISelectOption<string, string>[];
    visitorStartDate?: DateTime;
    visitorEndDate?: DateTime;
    onChange: (props: IVisitorChange) => void;
    onValidate: (isValid: boolean) => void;
    onLoading?: () => void;
    onLoaded?: () => void;
    buildingOptions?: ISelectOption<string, number>[];
    buildingChanged?: (buildingId: string) => void;
    disableFields?: boolean;
    emailNotRequired?: boolean;
}

export interface IVisitorChange
{
    firstName?: string;
    lastName?: string;
    company?: string;
    email?: string;
    visitorTypeId?: string;
    pacs?: string;
    notes?: INote[];
    hostEmail?: string;
    host?: IUser | null;
    visitorStartDate?: DateTime;
    visitorEndDate?: DateTime;
    arrivalLocationId?: string;
}

export interface IState
{
    visitorTypes: ISelectOption<string, string>[];
    areVisitorDetailsValid: boolean;
    areHostDetailsValid: boolean;
}

export class VisitorFieldHelper
{
    public fields: VisitorField;

    constructor(fields?: VisitorField)
    {
        this.fields = fields ?? (VisitorField.Name | VisitorField.Company | VisitorField.Email | VisitorField.VisitorType | VisitorField.Pacs | VisitorField.Notes | VisitorField.Host | VisitorField.ArrivalLocation | VisitorField.Times);
    }

    public has(field: VisitorField): boolean
    {
        return ((this.fields & field) == field);
    }
}

export enum VisitorField
{
    Name = 1 << 0,
    Company = 1 << 1,
    Email = 1 << 2,
    VisitorType = 1 << 3,
    Pacs = 1 << 4,
    Notes = 1 << 5,
    Host = 1 << 6,
    ArrivalLocation = 1 << 7,
    Times = 1 << 8,
}
